import React, { useState, useEffect, useCallback } from 'react'
import DashboardLayout from '../../../layouts/Dashboard'
import { Col, Row, Button, Badge, Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { Plus, Warning } from 'phosphor-react'
import api from '../../../services/api'
import { toast } from 'react-toastify'
import styles from './styles.module.css'
import { useLoading } from '../../../hooks/LoadingContext'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import SearchInput from '../../../components/SearchInput'

interface IVehicle {
  board: string
  model: string
  id: string
  capacity: number
  count_requests_delivered: number
  color: string
  driver_name: string
  validated: boolean
}

const Veiculos: React.FC = () => {
  const [vehicles, setVehicles] = useState<IVehicle[]>()
  const { isLoading, setIsLoading } = useLoading()
  const [hasMoreData, setHasMoreData] = useState<boolean>(true)
  const [search, setSearch] = useState('')
  const [lastSearch, setLastSearch] = useState('')

  const navigate = useNavigate()

  const loadData = useCallback(async () => {
    const isSearchDifferent = search !== lastSearch
    if (!hasMoreData && !isSearchDifferent) return

    setIsLoading(true)

    if (isSearchDifferent) {
      setLastSearch(search)
      setHasMoreData(true)
    }

    let apiUrl = '/company/vehicles?driverInfo=true&limit=12&show_invalid=true'
    if (search) apiUrl += '&board=' + search

    const offset = vehicles && !isSearchDifferent ? vehicles.length : 0
    if (offset) apiUrl += '&offset=' + offset

    try {
      const { data } = await api.get(apiUrl)
      if (!data.length) {
        if (isSearchDifferent) setVehicles(data)
        setHasMoreData(false)
      } else {
        if (!vehicles) setVehicles(data)
        else setVehicles(isSearchDifferent ? data : vehicles.concat(data))
      }
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
    }
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, vehicles, hasMoreData])

  useEffect(() => {
    if (!vehicles && hasMoreData) loadData()
  }, [loadData, hasMoreData, vehicles])

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop !==
          document.documentElement.offsetHeight ||
        isLoading
      ) {
        return
      }
      loadData()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoading, loadData])

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Veículos">
      <LoadingSpinner text="Buscando veículos. Aguarde..." />
      <Row style={{ marginInline: 25 }}>
        <SearchInput
          showOptions={false}
          data={vehicles}
          setValue={setSearch}
          handleClear={() => setSearch('')}
          placeholder="Digite a placa do veículo"
          handleSearch={loadData}
          searchValue={search}
          edit
        />
        <Col
          style={{ display: 'flex', justifyContent: 'right', marginInline: 30 }}
          className="justify-content-md-end justify-content-center"
        >
          <Button
            className={`${styles.new_vehicle_button} px-3 px-md-5`}
            onClick={() => navigate('/empresa/veiculos/novo')}
          >
            <Plus
              style={{ marginBottom: 4, marginRight: 4 }}
              color="#fff"
              size={15}
              weight="bold"
            />
            Novo veículo
          </Button>
        </Col>
      </Row>
      <Row
        style={{ marginInline: 30, display: 'flex' }}
        className="justify-content-center justify-content-md-start"
      >
        {isLoading && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 40,
            }}
          >
            <Spinner
              animation="border"
              style={{
                color: '#053C5E',
                marginRight: 10,
              }}
            />
          </div>
        )}
        {!vehicles?.length && !isLoading && (
          <Badge
            bg=""
            text="white"
            style={{ textAlign: 'center', marginTop: 40, backgroundColor: '#053c5e' }}
          >
            <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" /> Nenhum
            veículo encontrado!
          </Badge>
        )}
        {vehicles?.map((vehicle, index) => (
          <Col key={index} className={styles.vehicleCard} title="Veículo" md={3}>
            <div>
              <span className={styles.vehicle_board}>{vehicle.board}</span>
              <span style={{ display: 'block', fontWeight: '400', fontSize: 15 }}>
                {`${vehicle.model} ${vehicle.color}`}
              </span>

              {!vehicle.validated && (
                <div>
                  <Badge bg="danger" style={{ marginTop: 5, fontSize: 10 }}>
                    Veículo não validado
                  </Badge>
                </div>
              )}
              <span className={styles.vehicle_info}>
                Capacidade:{' '}
                <span
                  style={{
                    fontWeight: '400',
                    fontSize: 15,
                  }}
                >
                  {vehicle.capacity} kg
                </span>
              </span>
              <span className={styles.vehicle_info}>
                Último motorista:{' '}
                <span
                  style={{
                    fontWeight: '400',
                    fontSize: 15,
                  }}
                >
                  {vehicle.driver_name || 'Desconhecido'}
                </span>
              </span>
              <span className={styles.vehicle_info}>
                Total de entregas:{' '}
                <span
                  style={{
                    fontWeight: '400',
                    fontSize: 15,
                  }}
                >
                  {vehicle.count_requests_delivered}
                </span>
              </span>
            </div>
            <div>
              <Button
                className={styles.card_button}
                style={{ marginTop: 13 }}
                onClick={() => navigate(`/empresa/veiculos/editar/${vehicle.id}`)}
              >
                Editar
              </Button>
              <Button
                className={styles.card_button}
                onClick={() => navigate(`/empresa/veiculos/info/${vehicle.id}/entregas-do-dia`)}
              >
                Mais informações
              </Button>
            </div>
          </Col>
        ))}
      </Row>
    </DashboardLayout>
  )
}

export default Veiculos
